import { useEffect, useState } from "react";
import { Loader2, PhoneCall, Clock, CalendarCheck, TrendingUp } from "lucide-react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { adminApi } from "@/lib/adminApi";
import { StatCard, CHART_COLORS } from "@/components/admin/_shared";

export default function AdminAnalytics() {
  const [d, setD] = useState(null);
  useEffect(() => { adminApi.get("/admin/analytics").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div data-testid="admin-analytics">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Platform Analytics</h1>
      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="an-conv" label="Trial Conversion Rate" value={`${d.trial_conversion_rate}%`} icon={TrendingUp} accent="emerald" />
        <StatCard testid="an-dur" label="Avg Call Duration" value={`${d.avg_call_duration}s`} icon={Clock} accent="blue" />
        <StatCard testid="an-appts" label="Appointments Booked" value={d.appointments} icon={CalendarCheck} accent="violet" />
        <StatCard testid="an-min" label="Minutes Consumed" value={d.minutes_consumed.toLocaleString()} icon={PhoneCall} accent="amber" />
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading font-bold tracking-tight mb-4">Daily calls (last 14 days)</h2>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={d.daily_calls}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" fontSize={11} tickLine={false} /><YAxis fontSize={11} tickLine={false} allowDecimals={false} />
              <Tooltip /><Line type="monotone" dataKey="calls" stroke="#2563eb" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading font-bold tracking-tight mb-4">Most popular industries</h2>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={d.industries} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {d.industries.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie><Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading font-bold tracking-tight mb-4">Subscription funnel</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.subscription_growth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="stage" fontSize={11} tickLine={false} /><YAxis fontSize={11} tickLine={false} allowDecimals={false} />
              <Tooltip /><Bar dataKey="value" radius={[6, 6, 0, 0]}>{d.subscription_growth.map((e, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h2 className="font-heading font-bold tracking-tight mb-4">Top customers by usage</h2>
          <div className="space-y-2">
            {d.top_customers.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-slate-50 pb-2">
                <div><p className="font-semibold">{c.business_name}</p><p className="text-xs text-slate-400">{c.owner}</p></div>
                <span className="font-heading font-bold">{c.minutes_used} min</span>
              </div>
            ))}
            {d.top_customers.length === 0 && <p className="text-sm text-slate-400">No usage data yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
